// Thin REST client. Cookies are sent automatically (same-origin). No bearer tokens.

export interface Me {
  id: string;
  display_name: string;
  role: 'learner' | 'instructor' | 'admin' | 'superadmin';
  region_default: string;
}

async function req<T>(method: string, path: string, body?: any): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${txt || res.statusText}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  me: () => req<{ user: Me }>('GET', '/api/auth/me').then((r) => r.user).catch(() => null),
  requestMagicLink: (email: string) => req<{ ok: true; dev_link?: string }>('POST', '/api/auth/request', { email }),
  logout: () => req<{ ok: true }>('POST', '/api/auth/logout'),
  startDemo: () => req<{ ok: true; scenario_id: string; display_name: string }>('POST', '/api/demo/start', {}),
  mcqStart: (count = 10) => req<{ region: string; pass_threshold_pct: number; questions: Array<{ id: string; topic: string; stem: string; options: string[] }> }>('GET', `/api/mcq/start?count=${count}`),
  mcqSubmit: (answers: Array<{ question_id: string; chosen_index: number }>) =>
    req<{ total: number; correct: number; score_pct: number; pass_threshold_pct: number; passed: boolean; breakdown: any[]; attempted_at: number }>('POST', '/api/mcq/submit', { answers }),
  mcqHistory: () => req<{ sessions: Array<{ attempted_at: number; total: number; correct: number; score_pct: number; passed: boolean }> }>('GET', '/api/mcq/history'),
  mcqCertificateUrl: (attempted_at: number) => `/api/mcq/certificate.pdf?attempted_at=${attempted_at}`,
  runEvidencePackUrl: (run_id: string) => `/api/runs/${run_id}/cpd-evidence.pdf`,
  scenarios: (region?: string) => req<{ scenarios: any[] }>('GET', `/api/scenarios${region ? `?region=${region}` : ''}`),
  scenarioById: (id: string) => req<{ scenario: any }>('GET', `/api/scenarios/${id}`),
  myRuns: () => req<{ runs: any[] }>('GET', '/api/runs'),
  runById: (id: string) => req<{ run: any; scenario: any; debrief: any; events: any[]; feedback: any[]; vitals: any[] }>('GET', `/api/runs/${id}`),
  adminStats: () => req<any>('GET', '/api/admin/stats'),
  adminUsers: () => req<{ users: any[] }>('GET', '/api/admin/users'),
  adminUpdateUser: (id: string, patch: { role?: string; pro_role?: string; region_default?: string; status?: string }) =>
    req<{ ok: true }>('PATCH', `/api/admin/users/${id}`, patch),
  adminRevokeUserSessions: (id: string) => req<{ ok: true; revoked: number }>('DELETE', `/api/admin/users/${id}/sessions`),
  adminRuns: (params?: { outcome?: string; region?: string; user_id?: string; scenario_id?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => v != null && q.set(k, String(v)));
    const qs = q.toString();
    return req<{ runs: any[] }>('GET', `/api/admin/runs${qs ? `?${qs}` : ''}`);
  },
  adminRunsCsvUrl: () => '/api/admin/runs.csv',
  adminScenarios: () => req<{ scenarios: any[] }>('GET', '/api/admin/scenarios'),
  adminScenarioStats: () => req<{ scenarios: any[] }>('GET', '/api/admin/scenario-stats'),
  adminFeedbackStats: (region?: string) => req<{ feedback: any[] }>('GET', `/api/admin/feedback-stats${region ? `?region=${region}` : ''}`),
  adminAudit: () => req<{ audit: any[] }>('GET', '/api/admin/audit'),
  adminCohorts: () => req<{ cohorts: any[] }>('GET', '/api/admin/cohorts'),
  adminCohortCreate: (name: string, region?: string, cohort_code?: string) => req<{ ok: true; id: string }>('POST', '/api/admin/cohorts', { name, region, cohort_code }),
  adminCohortDetail: (id: string) => req<{ cohort: any; members: any[] }>('GET', `/api/admin/cohorts/${id}`),
  adminCohortAddMember: (id: string, user_id: string) => req<{ ok: true }>('POST', `/api/admin/cohorts/${id}/members`, { user_id }),
  adminCohortRemoveMember: (id: string, user_id: string) => req<{ ok: true }>('DELETE', `/api/admin/cohorts/${id}/members/${user_id}`),
  adminCohortDelete: (id: string) => req<{ ok: true }>('DELETE', `/api/admin/cohorts/${id}`),
  adminCohortExportXlsxUrl: (id: string) => `/api/admin/cohorts/${id}/export.xlsx`,
  adminCohortReportPdfUrl: (id: string) => `/api/admin/cohorts/${id}/report.pdf`,
  adminDebrief: (run_id: string) => req<{ debrief: any; events: any[]; feedback: any[] }>('GET', `/api/admin/runs/${run_id}/debrief`),
  adminPublishScenario: (id: string) => req<{ ok: true }>('POST', `/api/admin/scenarios/${id}/publish`),
  adminRetireScenario: (id: string) => req<{ ok: true }>('POST', `/api/admin/scenarios/${id}/retire`),
};
